import {
  AfterViewInit,
  Directive,
  EventEmitter,
  Host,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

import { DziViewerComponent } from '../../dzi-viewer.component';
import OpenSeadragon from 'openseadragon';
export interface OverlayData {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color?: string;
  selected?: boolean;
  tooltip?: string;
}
@Directive({
  selector: '[tsDziOverlaysViewer]',
  standalone: false,
})
export class DziOverlaysViewerDirective implements OnChanges, AfterViewInit {
  @Input() overlays: OverlayData[] = [];
  /** IDs of ROIs to emphasise (their label is hovered in the panel). Kept as a
   *  SEPARATE input from `overlays` so toggling the highlight only restyles the
   *  existing overlay elements instead of re-rendering all of them — important
   *  with hundreds of ROIs. */
  @Input() highlightedIds: Set<string> = new Set();
  @Output() overlaysRendered = new EventEmitter<void>();

  private corners = ['tl', 'tr', 'bl', 'br'];
  private csize = 8;
  private tooltipEl: HTMLDivElement | null = null;
  /** Live references to the rendered overlay elements, by ROI id, so highlight
   *  changes can restyle them without a full clear + rebuild. */
  private overlayElements = new Map<string, HTMLElement>();

  constructor(@Host() private dziViewer: DziViewerComponent, private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Structural change → full (re)render. This also applies the current
    // highlight at the end.
    if (changes.overlays) {
      if (!this.overlays) return;
      this.updateOverlays();
      return;
    }

    // Highlight-only change → cheap restyle of existing elements, no rebuild.
    if (changes.highlightedIds) {
      this.applyHighlightAll();
    }
  }

  ngAfterViewInit(): void {
    this.dziViewer['viewer'].addHandler('open', () => {
      this.updateOverlays();
    });
  }

  private updateOverlays(): void {
    if (!this.dziViewer || !this.dziViewer['viewer']) return;

    const viewer = this.dziViewer['viewer'];

    if (viewer.world.getItemCount() === 0) return;

    this.hideTooltip();

    viewer.clearOverlays();
    this.overlayElements.clear();

    this.overlays.forEach((overlay) => {
      const overlayContainer = this.renderer.createElement('div');
      this.renderer.setAttribute(overlayContainer, 'id', overlay.id);

      // Set tooltip if available
      if (overlay.tooltip) {
        // Use custom tooltip for instant display
        this.addTooltipHandlers(overlayContainer, overlay.tooltip);
      }

      const bounds =
        overlay.w && overlay.h
          ? viewer.viewport.imageToViewportRectangle(overlay.x, overlay.y, overlay.w, overlay.h)
          : viewer.viewport.imageToViewportCoordinates(overlay.x, overlay.y);

      if (overlay.w && overlay.h) {
        this.addROIStyle(overlayContainer, overlay.color, overlay.selected);
      } else {
        this.addPOIStyle(overlayContainer, overlay.color, overlay.selected);
      }

      this.overlayElements.set(overlay.id, overlayContainer);
      viewer.addOverlay(overlayContainer, bounds);
    });

    // Apply the current highlight to the freshly created elements.
    this.applyHighlightAll();

    this.overlaysRendered.emit();
  }

  /** Restyle existing overlay elements to reflect the current highlight set,
   *  without rebuilding any DOM (cheap — safe for hundreds of ROIs). */
  private applyHighlightAll(): void {
    const ids = this.highlightedIds ?? new Set<string>();
    const hasHighlight = ids.size > 0;
    this.overlayElements.forEach((element, id) => {
      this.applyHighlight(element, hasHighlight, ids.has(id));
    });
  }

  /** Emphasise the highlighted overlays and dim the rest while a label is
   *  hovered. Both paths only touch inline styles/classList — no DOM mutation. */
  private applyHighlight(element: HTMLElement, hasHighlight: boolean, highlighted: boolean): void {
    this.renderer.setStyle(element, 'transition', 'opacity 0.15s ease');

    const isPoi = element.classList.contains('poi-overlay');

    if (!hasHighlight) {
      this.renderer.setStyle(element, 'opacity', '1');
      this.clearHighlightDecoration(element, isPoi);
      return;
    }

    if (highlighted) {
      this.renderer.setStyle(element, 'opacity', '1');
      if (isPoi) {
        this.renderer.addClass(element, 'poi-overlay--highlighted');
      } else {
        this.renderer.setStyle(element, 'outline', '3px solid #fff');
        this.renderer.setStyle(element, 'outline-offset', '1px');
      }
    } else {
      this.renderer.setStyle(element, 'opacity', '0.25');
      this.clearHighlightDecoration(element, isPoi);
    }
  }

  private clearHighlightDecoration(element: HTMLElement, isPoi: boolean): void {
    if (isPoi) {
      this.renderer.removeClass(element, 'poi-overlay--highlighted');
    } else {
      this.renderer.removeStyle(element, 'outline');
      this.renderer.removeStyle(element, 'outline-offset');
    }
  }

  private addTooltipHandlers(element: HTMLElement, text: string): void {
    const onEnter = (e: MouseEvent) => {
      this.showTooltip(text, e.clientX, e.clientY);
      element.addEventListener('mousemove', onMove);
    };

    const onLeave = () => {
      element.removeEventListener('mousemove', onMove);
      this.hideTooltip();
    };

    const onMove = (e: MouseEvent) => {
      this.positionTooltip(e.clientX, e.clientY);
    };

    element.addEventListener('mouseenter', onEnter);
    element.addEventListener('mouseleave', onLeave);
  }

  private showTooltip(text: string, x: number, y: number): void {
    if (!this.tooltipEl) {
      this.tooltipEl = this.renderer.createElement('div');
      this.renderer.setStyle(this.tooltipEl, 'position', 'fixed');
      this.renderer.setStyle(this.tooltipEl, 'pointer-events', 'none');
      this.renderer.setStyle(this.tooltipEl, 'z-index', '10000');
      this.renderer.setStyle(this.tooltipEl, 'background', 'rgba(128, 128, 128, 0.9)');
      this.renderer.setStyle(this.tooltipEl, 'color', '#fff');
      this.renderer.setStyle(this.tooltipEl, 'padding', '2px 6px');
      this.renderer.setStyle(this.tooltipEl, 'border-radius', '3px');
      this.renderer.setStyle(this.tooltipEl, 'font-size', '11px');
      this.renderer.appendChild(document.body, this.tooltipEl);
    }
    this.tooltipEl.textContent = text;
    this.positionTooltip(x, y);
    this.renderer.setStyle(this.tooltipEl, 'display', 'block');
  }

  private positionTooltip(x: number, y: number): void {
    if (!this.tooltipEl) return;
    const offset = 10;
    this.renderer.setStyle(this.tooltipEl, 'left', `${x + offset}px`);
    this.renderer.setStyle(this.tooltipEl, 'top', `${y + offset}px`);
  }

  private hideTooltip(): void {
    if (!this.tooltipEl) return;
    this.renderer.setStyle(this.tooltipEl, 'display', 'none');
  }

  private addROIStyle(element: HTMLDivElement, color: string, selected: boolean) {
    if (selected) {
      this.renderer.setStyle(element, 'border-width', `3px`);
      this.renderer.setStyle(element, 'border-style', `solid`);
      this.renderer.setStyle(element, 'border-color', color);
      this.renderer.setStyle(element, 'box-shadow', `inset 0 0 0 1px red`);
      this.renderer.setStyle(element, 'z-index', '15');
    } else {
      this.renderer.setStyle(element, 'border-width', `2px`);
      this.renderer.setStyle(element, 'border-style', `solid`);
      this.renderer.setStyle(element, 'border-color', color);
      this.renderer.setStyle(element, 'box-shadow', 'none');
      this.renderer.setStyle(element, 'z-index', '10');
    }

    this.renderer.setStyle(element, 'border-radius', `${this.csize}px`);

    const boxStyle = new OpenSeadragon.MouseTracker({
      element: element,

      enterHandler: () => {
        if (!selected) {
          this.renderer.setStyle(element, 'border-color', 'white');
        }
      },

      exitHandler: () => {
        if (!selected) {
          this.renderer.setStyle(element, 'border-color', color);
        }
      },
    });

    boxStyle.setTracking(!selected);

    if (selected) this.addResizableBox(element);
  }

  private addPOIStyle(element: HTMLDivElement, color: string, selected: boolean) {
    this.renderer.addClass(element, 'poi-overlay'); // to make effective the translate operation for the icon
    this.renderer.addClass(element, selected ? 'ri-focus-3-fill' : 'ri-focus-3-line');

    this.renderer.setStyle(element, 'fontSize', '16px');
    this.renderer.setStyle(element, 'color', color);
    this.renderer.setStyle(element, 'z-index', '10');

    if (selected) this.addDashedCircle(element);
  }

  // No hover-driven style updates for now

  // TODO: For ROI edition

  private addResizableBox(element: HTMLDivElement) {
    this.renderer.setStyle(element, 'position', 'relative');
    this.renderer.setStyle(element, 'display', 'inline-block');
    this.renderer.setStyle(element, 'box-shadow', 'inset 0 0 0 1px red');
    this.renderer.setStyle(element, 'cursor', 'grab');

    this.corners.forEach((corner) => {
      const handle = this.renderer.createElement('div');

      const id = `${element.id}-${corner}-handle`;
      this.renderer.setAttribute(handle, 'id', id);

      this.renderer.setStyle(handle, 'position', 'absolute');
      this.renderer.setStyle(handle, 'width', `${this.csize}px`);
      this.renderer.setStyle(handle, 'height', `${this.csize}px`);
      this.renderer.setStyle(handle, 'background-color', 'white');
      this.renderer.setStyle(handle, 'z-index', '20');

      const offset = `${-(this.csize / 2)}px`;

      switch (corner) {
        case 'tl': // top-left
          this.renderer.setStyle(handle, 'top', offset);
          this.renderer.setStyle(handle, 'left', offset);
          this.renderer.setStyle(handle, 'cursor', 'nwse-resize');
          break;

        case 'tr': // top-right
          this.renderer.setStyle(handle, 'top', offset);
          this.renderer.setStyle(handle, 'right', offset);
          this.renderer.setStyle(handle, 'cursor', 'nesw-resize');
          break;

        case 'bl': // bottom-left
          this.renderer.setStyle(handle, 'bottom', offset);
          this.renderer.setStyle(handle, 'left', offset);
          this.renderer.setStyle(handle, 'cursor', 'nesw-resize');
          break;

        case 'br': // bottom-right
          this.renderer.setStyle(handle, 'bottom', offset);
          this.renderer.setStyle(handle, 'right', offset);
          this.renderer.setStyle(handle, 'cursor', 'nwse-resize');
          break;
      }

      const cornerStyle = new OpenSeadragon.MouseTracker({
        element: handle,

        enterHandler: () => {
          this.renderer.setStyle(handle, 'background-color', 'black');
          this.renderer.setStyle(handle, 'border', '1px solid white');
        },

        exitHandler: () => {
          this.renderer.setStyle(handle, 'background-color', 'white');
          this.renderer.setStyle(handle, 'border', '1px solid white');
        },
      });

      element.appendChild(handle);
      cornerStyle.setTracking(true);
    });
  }

  private addDashedCircle(element: HTMLDivElement) {
    this.renderer.removeClass(element, 'ri-focus-3-line');
    this.renderer.addClass(element, 'ri-focus-3-fill');

    const circle = this.renderer.createElement('div');
    const csize = 32;

    this.renderer.setStyle(circle, 'position', 'absolute');
    this.renderer.setStyle(circle, 'top', '50%');
    this.renderer.setStyle(circle, 'left', '50%');
    this.renderer.setStyle(circle, 'width', `${csize}px`);
    this.renderer.setStyle(circle, 'height', `${csize}px`);
    this.renderer.setStyle(circle, 'border', '2px dashed white');
    this.renderer.setStyle(circle, 'border-radius', '50%');
    this.renderer.setStyle(circle, 'pointer-events', 'none');
    this.renderer.setStyle(circle, 'transform', 'translate(-50%, -50%)');

    this.renderer.appendChild(element, circle);
  }
}
